import { Check, CheckCheck, Image, Video, Mic, Camera, FileText, MapPin, User } from 'lucide-react';

export default function ConversationList({ conversations, activeId, onSelect, loading, currentUserId, typingUsers = {} }) {
  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="conversation-list-loading">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="flex items-center gap-3 px-3 py-3 animate-pulse">
            <div className="w-[52px] h-[52px] rounded-full bg-slate-200 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-slate-200 rounded-lg w-28" />
              <div className="h-3 bg-slate-100 rounded-lg w-44" />
            </div>
            <div className="h-3 bg-slate-100 rounded w-10 flex-shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6" data-testid="conversation-list-empty">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl text-[#FF9933]" style={{ fontFamily: "'Yatra One', cursive" }}>Om</span>
          </div>
          <p className="text-slate-700 font-semibold text-base">Start a blessed conversation</p>
          <p className="text-xs text-slate-400 mt-1.5 max-w-[200px] mx-auto">Connect with your DharmBook community</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
      {conversations.map((conv, idx) => (
        <ConversationItem
          key={conv.id}
          conv={conv}
          isActive={conv.id === activeId}
          onSelect={onSelect}
          currentUserId={currentUserId}
          isTyping={!!typingUsers[conv.id]}
          index={idx}
        />
      ))}
    </div>
  );
}

function ConversationItem({ conv, isActive, onSelect, currentUserId, isTyping, index }) {
  const hasUnread = conv.unread_count > 0;
  const timeStr = conv.last_message_time ? formatTimeShort(conv.last_message_time) : '';
  const isMySentMessage = conv.last_sender_id && conv.last_sender_id !== conv.other_user_id;

  return (
    <button
      onClick={() => onSelect(conv)}
      className={`group w-full flex items-center gap-3 px-4 py-3 transition-all duration-200 relative
        ${isActive
          ? 'bg-orange-50/80'
          : 'hover:bg-slate-50/80 active:bg-slate-100'
        }
      `}
      style={{ animationDelay: `${index * 30}ms` }}
      data-testid={`conversation-${conv.id}`}
    >
      {/* Active indicator */}
      {isActive && (
        <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-[#FF9933]" />
      )}

      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <img
          src={conv.other_user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conv.other_user_name || 'U')}&background=FF9933&color=fff&size=52`}
          alt=""
          className="w-[52px] h-[52px] rounded-full object-cover ring-2 ring-transparent group-hover:ring-orange-100 transition-all"
        />
        {conv.other_user_online && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-[2.5px] border-white shadow-sm" data-testid={`online-dot-${conv.id}`} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 py-0.5">
        <div className="flex items-center justify-between mb-0.5">
          <h3 className={`text-[15px] truncate leading-tight ${hasUnread ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`} data-testid={`conv-name-${conv.id}`}>
            {conv.other_user_name}
          </h3>
          <span className={`text-[11px] flex-shrink-0 ml-2 tabular-nums ${hasUnread ? 'text-[#FF9933] font-bold' : 'text-slate-400'}`} data-testid={`conv-time-${conv.id}`}>
            {timeStr}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 flex-1 min-w-0">
            {/* Read receipts for sent messages */}
            {isMySentMessage && !isTyping && (
              <span className="flex-shrink-0" data-testid={`msg-status-${conv.id}`}>
                {conv.last_message_status === 'seen'
                  ? <CheckCheck className="w-4 h-4 text-blue-500" />
                  : conv.last_message_status === 'delivered'
                    ? <CheckCheck className="w-4 h-4 text-slate-400" />
                    : <Check className="w-4 h-4 text-slate-400" />
                }
              </span>
            )}
            {/* Typing indicator or last message */}
            {isTyping ? (
              <TypingIndicator />
            ) : (
              <p className={`text-[13px] truncate leading-snug ${hasUnread ? 'text-slate-700 font-medium' : 'text-slate-500'}`} data-testid={`conv-preview-${conv.id}`}>
                <MessagePreview conv={conv} />
              </p>
            )}
          </div>
          {/* Unread badge */}
          {hasUnread && (
            <span
              className="ml-2 flex-shrink-0 bg-[#FF9933] text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center shadow-sm"
              data-testid={`unread-badge-${conv.id}`}
            >
              {conv.unread_count > 99 ? '99+' : conv.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function MessagePreview({ conv }) {
  if (!conv.last_message && !conv.last_message_type) {
    return <span className="italic text-slate-400">Start a conversation</span>;
  }

  // Deleted message
  if (conv.last_message === '[Message deleted]') {
    return <span className="italic text-slate-400">Message deleted</span>;
  }

  // Media type indicators
  const mediaType = conv.last_message_type;
  if (mediaType === 'image' || conv.last_message === '[Image]') {
    return (
      <span className="flex items-center gap-1">
        <Camera className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span>{conv.last_message && conv.last_message !== '[Image]' ? conv.last_message : 'Photo'}</span>
      </span>
    );
  }
  if (mediaType === 'video') {
    return (
      <span className="flex items-center gap-1">
        <Video className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span>Video</span>
      </span>
    );
  }
  if (mediaType === 'audio' || mediaType === 'voice') {
    return (
      <span className="flex items-center gap-1">
        <Mic className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span>Voice message</span>
      </span>
    );
  }
  if (mediaType === 'document') {
    let docName = 'Document';
    try { const d = JSON.parse(conv.last_message); docName = d.filename || 'Document'; } catch {}
    return (
      <span className="flex items-center gap-1">
        <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className="truncate">{docName}</span>
      </span>
    );
  }
  if (mediaType === 'location') {
    return (
      <span className="flex items-center gap-1">
        <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span>Location</span>
      </span>
    );
  }
  if (mediaType === 'contact') {
    let contactName = 'Contact';
    try { const c = JSON.parse(conv.last_message); contactName = c.name || 'Contact'; } catch {}
    return (
      <span className="flex items-center gap-1">
        <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className="truncate">{contactName}</span>
      </span>
    );
  }

  return conv.last_message || 'Start a conversation';
}

function TypingIndicator() {
  return (
    <span className="flex items-center gap-0.5 text-[13px] text-[#FF9933] font-medium" data-testid="typing-indicator">
      <span>typing</span>
      <span className="flex items-end gap-[2px] ml-0.5 h-4">
        <span className="w-[4px] h-[4px] bg-[#FF9933] rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
        <span className="w-[4px] h-[4px] bg-[#FF9933] rounded-full animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1s' }} />
        <span className="w-[4px] h-[4px] bg-[#FF9933] rounded-full animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1s' }} />
      </span>
    </span>
  );
}

function formatTimeShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-IN', { weekday: 'short' });
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
